import { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, KeyRound, Mail, Sparkles, Home } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { useAuth, homeFor } from "@/lib/auth";
import api from "@/lib/api";

export default function Login() {
  const { loginPassword, requestOtp, verifyOtp, toggleTheme, theme, isAuthed, user } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const from = loc.state?.from;

  useEffect(() => {
    if (isAuthed && user) nav(from || homeFor(user), { replace: true });
  }, [isAuthed, user, from, nav]);

  const [tab, setTab] = useState("password");
  const [busy, setBusy] = useState(false);

  // shared

  // password
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  // otp
  const [otpEmail, setOtpEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [otpStep, setOtpStep] = useState("email"); // email | code


  const onPasswordSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await loginPassword(email.trim(), password);
      toast.success(`Welcome, ${u.name || u.email}`);
      nav(from || homeFor(u), { replace: true });
    } catch (e) {
      toast.error(e.message || "Login failed");
    } finally { setBusy(false); }
  };

  const onOtpRequest = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await requestOtp(otpEmail.trim());
      toast.success("Code sent — check your email");
      setOtpStep("code");
    } catch (e) {
      toast.error(e.message || "Could not send OTP");
    } finally { setBusy(false); }
  };

  const onOtpVerify = async (e) => {
    e.preventDefault();
    if (otp.length < 6) { toast.error("Enter the 6-digit code"); return; }
    setBusy(true);
    try {
      const u = await verifyOtp(otpEmail.trim(), otp);
      toast.success(`Welcome, ${u.name || u.email}`);
      nav(from || homeFor(u), { replace: true });
    } catch (e) {
      toast.error(e.message || "Invalid or expired code");
    } finally { setBusy(false); }
  };

  return (
    <div className="relative min-h-screen overflow-hidden">
      <div className="orb bg-cobalt-500/50" style={{ width: 520, height: 520, top: -160, left: -180 }} />
      <div className="orb bg-cyanx/40"   style={{ width: 460, height: 460, top: 60,   right: -120 }} />
      <div className="orb bg-chartreuse/40" style={{ width: 380, height: 380, bottom: -160, left: "35%" }} />
      <div className="absolute inset-0 grid-lines pointer-events-none" />

      <header className="relative z-10 flex items-center justify-between px-4 sm:px-8 lg:px-14 py-4 sm:py-6 safe-top">
        <Link to="/" className="flex items-center gap-2.5" data-testid="login-brand">
          <div className="h-8 w-8 rounded-xl bg-white border border-black/5 grid place-items-center shrink-0 shadow-sm"><img src="/brand/solution-mark-t.png" alt="Solution" className="h-6 w-6 object-contain" /></div>
          <div className="font-display text-xl font-extrabold tracking-tighter">
            Solu<span className="text-cobalt-500">tion</span>
          </div>
        </Link>
        <div className="flex items-center gap-2">
          <Link
            to="/"
            data-testid="login-home"
            className="glass rounded-full px-3 sm:px-4 py-1.5 text-xs font-medium flex items-center gap-1.5 tap-shrink"
          >
            <Home className="h-3.5 w-3.5" /> Home
          </Link>
          <button
            data-testid="login-theme-toggle"
            onClick={toggleTheme}
            className="glass rounded-full px-3 sm:px-4 py-1.5 text-xs font-medium tap-shrink"
          >
            {theme === "dark" ? "Light" : "Dark"}
          </button>
        </div>
      </header>

      <main className="relative z-10 mx-auto max-w-6xl px-4 sm:px-6 lg:px-14 pt-4 sm:pt-6 pb-10 sm:pb-16 grid lg:grid-cols-[1.05fr_0.95fr] gap-8 lg:gap-14 items-center">
        <div className="hidden lg:block">
          <div className="glass inline-flex items-center gap-1.5 rounded-full px-3 py-1 border-white/60 dark:border-white/10">
            <Sparkles className="h-3 w-3 text-cobalt-500" />
            <span className="text-mono text-[10px] tracking-widest uppercase">Solution · Sign in</span>
          </div>
          <h1 className="mt-6 font-display font-black leading-[0.95] tracking-tighter text-[52px] sm:text-[64px] lg:text-[80px]">
            One code.<br />
            <span className="italic font-light">Every ticket,</span><br />
            <span className="bg-clip-text text-transparent bg-[linear-gradient(120deg,#0047FF_0%,#22D3EE_60%,#BEF264_100%)]">
              start to finish.
            </span>
          </h1>
          <p className="mt-6 max-w-md text-lg text-muted-foreground leading-relaxed">
            Sign in to your campus service desk. Passwordless OTP or your institute password — whichever your admin allows.
          </p>
        </div>

        {/* Compact intro shown only on phones/tablets, above the card */}
        <div className="lg:hidden text-center">
          <div className="glass inline-flex items-center gap-1.5 rounded-full px-3 py-1 border-white/60 dark:border-white/10">
            <Sparkles className="h-3 w-3 text-cobalt-500" />
            <span className="text-mono text-[10px] tracking-widest uppercase">Solution · Sign in</span>
          </div>
          <h1 className="mt-4 font-display font-black leading-[1] tracking-tighter text-[32px] sm:text-[40px]">
            One code, every ticket.
          </h1>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
          className="glass-strong rounded-3xl p-5 sm:p-8"
          data-testid="login-card"
        >
          <Tabs value={tab} onValueChange={(v) => { setTab(v); setOtpStep("email"); }} data-testid="login-tabs">
            <TabsList className="grid grid-cols-2 rounded-full bg-white/60 dark:bg-white/5 border border-white/60 dark:border-white/10 p-1 w-full">
              <TabsTrigger value="password" className="rounded-full" data-testid="tab-password">
                <KeyRound className="h-3.5 w-3.5 mr-1.5" /> Password
              </TabsTrigger>
              <TabsTrigger value="otp" className="rounded-full" data-testid="tab-otp">
                <Mail className="h-3.5 w-3.5 mr-1.5" /> Email code
              </TabsTrigger>
            </TabsList>

            <TabsContent value="password" className="mt-5">
              <form onSubmit={onPasswordSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    data-testid="login-email"
                    placeholder="you@institute.edu"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mt-1.5 rounded-xl"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    autoComplete="current-password"
                    data-testid="login-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="mt-1.5 rounded-xl"
                    required
                  />
                </div>
                <Button
                  type="submit"
                  data-testid="login-submit"
                  disabled={busy}
                  className="w-full rounded-full h-11 bg-cobalt-500 hover:bg-cobalt-600 text-white gap-1.5"
                >
                  {busy ? "Signing in…" : (<>Sign in <ArrowRight className="h-4 w-4" /></>)}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="otp" className="mt-5">
              {otpStep === "email" ? (
                <form onSubmit={onOtpRequest} className="space-y-4">
                  <div>
                    <Label htmlFor="otpEmail">Email</Label>
                    <Input
                      id="otpEmail"
                      type="email"
                      data-testid="otp-email"
                      placeholder="you@institute.edu"
                      value={otpEmail}
                      onChange={(e) => setOtpEmail(e.target.value)}
                      className="mt-1.5 rounded-xl"
                      required
                    />
                  </div>
                  <Button
                    type="submit"
                    data-testid="otp-send"
                    disabled={busy}
                    className="w-full rounded-full h-11 bg-cobalt-500 hover:bg-cobalt-600 text-white gap-1.5"
                  >
                    {busy ? "Sending…" : (<>Send code <ArrowRight className="h-4 w-4" /></>)}
                  </Button>
                </form>
              ) : (
                <form onSubmit={onOtpVerify} className="space-y-4">
                  <div>
                    <Label>Enter the 6-digit code sent to {otpEmail}</Label>
                    <div className="mt-2 flex justify-center">
                      <InputOTP maxLength={6} value={otp} onChange={setOtp} data-testid="otp-code">
                        <InputOTPGroup>
                          {["s0","s1","s2","s3","s4","s5"].map((slotKey, i) => (
                            <InputOTPSlot key={slotKey} index={i} className="h-11 w-11 text-lg font-display" />
                          ))}
                        </InputOTPGroup>
                      </InputOTP>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="ghost"
                      className="rounded-full flex-1"
                      onClick={() => { setOtpStep("email"); setOtp(""); }}
                    >
                      Back
                    </Button>
                    <Button
                      type="submit"
                      data-testid="otp-verify"
                      disabled={busy || otp.length < 6}
                      className="rounded-full flex-1 bg-cobalt-500 hover:bg-cobalt-600 text-white"
                    >
                      {busy ? "Verifying…" : "Verify"}
                    </Button>
                  </div>
                </form>
              )}
            </TabsContent>
          </Tabs>

          <p className="mt-5 text-xs text-muted-foreground text-center">
            New here? <Link to="/register" className="text-cobalt-500 hover:underline font-medium">Create an account</Link>
          </p>
        </motion.div>
      </main>
    </div>
  );
}
